import React from 'react'
import ApiCustomer from '../code/api/ApiCustomer';
import '../css/messagelogdatatable.css';
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { stringToDateTime } from '../code/helpers/stringToDate';
import CustomerLog from "../code/models/CustomerLog";
import AuthAdminUserModel from "../code/models/AuthAdminUserModel";
import MessageModalForm from "./modal-forms/MessageModalForm";

interface IMessageLogDataTable {
    currentUser: AuthAdminUserModel;
    customerId: number;
}

export default function MessageLogDataTable({ currentUser, customerId }: IMessageLogDataTable) {

    const [tableData, setTableData] = React.useState<CustomerLog[]>([]);
    const [editLog, setEditLog] = React.useState<CustomerLog | null>(null);
    const [topCount, setTopCount] = React.useState<number>(10);
    const [includeBatch, setIncludeBatch] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadData();
    }, [topCount, includeBatch]);

    function loadData() {
        ApiCustomer.getLogs(customerId, topCount, includeBatch).then(data => {
            setTableData(data ?? []);
        });
    }

    function ScrubAndPrepMessage(html: string) {
        let preppedHtml;
        if (/<\/?[a-z][\s\S]*>/i.test(html)) {
            preppedHtml = html;
        } else {
            preppedHtml = html
                .replace("&", "&amp;")
                .replace("<", "&lt;")
                .replace(">", "&gt;")
                .replace(/\r\n?|\n|\n/g, "<br>");
        }

        return (
            parse(DOMPurify.sanitize(preppedHtml))
        )
    }

    function getNumberOfDays(theDate: any) {
        const date1 = new Date(`${theDate}Z`);
        const date2 = new Date();
        // One day in milliseconds
        const oneDay = 86400000;
        // Calculating the time difference between two dates
        const diffInTime = date2.getTime() - date1.getTime();
        // Calculating the no. of days between two dates
        const diffInDays = Math.round(diffInTime / oneDay);
        return diffInDays;
    }

    function doEditMessage(rowData: CustomerLog) {
        if (rowData.adminId === currentUser.id) {
            setEditLog(rowData);
        }
    }

    function buildEditButton(rowData: CustomerLog) {
        if (rowData.adminId === currentUser.id) {
            return (
                <button className="btn btn-sm btn-outline-secondary float-end me-2" onClick={() => { doEditMessage(rowData); }}>Edit</button>
            )
        } else return;
    }

    function editMessageModalConfirm(isConfirm: boolean, message?: string) {
        if (isConfirm && !!editLog) {
            var updateItem = structuredClone(editLog);
            updateItem.message = message || "";
            ApiCustomer.updateLog(updateItem).then(res => {
                if (!!res) {
                    editLog.message = res.message;
                }
            }).finally(() => {
                setEditLog(null);
            });
        } else {
            setEditLog(null);
        }
    }

    function buildRow(rowData: any, index: number) {
        const rowClassData = "msg-datarow data";
        let rowClassMessage = "msg-datarow message";
        const daysOld = getNumberOfDays(rowData.messageDate);

        if (daysOld <= 14) {
            rowClassMessage += " recent";
        }

        return (
            <React.Fragment key={`fragment_messagerow_${index}`}>
                <tr key={`messagerow_${index}`} className={rowClassData}>
                    <td>{stringToDateTime(rowData.messageDate)}</td>
                    <td>{rowData.messageType}</td>
                    <td>{rowData.adminName}</td>
                    <td>{rowData.messageDataType}</td>
                    <td>{rowData.subject}{buildEditButton(rowData)}</td>
                </tr>
                <tr key={`messagerow_2_${index}`} className={rowClassMessage}>
                    <td colSpan={5}>{ScrubAndPrepMessage(rowData.message)}</td>
                </tr>
            </React.Fragment>
        )
    }

    function handleLoadMoreClick(): void {
        setTopCount(0);
    }

    function buildFooter(): React.ReactNode {
        return (
            <tfoot>
                <tr>
                    <td colSpan={5} className="msg-table-footer-row">
                        <button onClick={handleLoadMoreClick} className="btn btn-outline-primary">Load More</button>
                    </td>
                </tr>
            </tfoot>
        );
    }

    function hasMore(): boolean {
        return tableData.length >= topCount;
    }

    function buildTable() {
        return (
            <React.Fragment>
                <div key={"mldincbtch" + includeBatch}>
                    <div className="form-check form-switch col-2 pad-left-15 mt-1 pb-0 mb-0">
                        <input className="form-check-input" type="checkbox" id="checkIncludeBatch" autoComplete="off"
                            checked={includeBatch} onClick={() => setIncludeBatch(!includeBatch)} />
                        <label className="form-check-label" htmlFor="checkIncludeBatch">Include Batch Emails</label>
                    </div>
                </div>
                <div className="msg-customer-table-wrapper">
                    <table className="msg-customer-table">
                        <thead>
                            <tr className="msg-table-header-row">
                                <td className="msg-table-header-col">Date</td>
                                <td className="msg-table-header-col">Type</td>
                                <td className="msg-table-header-col">Admin</td>
                                <td className="msg-table-header-col">Message Type</td>
                                <td className="msg-table-header-col">Subject</td>
                            </tr>
                        </thead>
                        <tbody className="msg-table-body">
                            {tableData && tableData.map((rowData: any, index: number) => {
                                return buildRow(rowData, index);
                            })}
                        </tbody>
                        {hasMore() && buildFooter()}
                    </table>
                </div>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            {tableData && buildTable()}
            <MessageModalForm key={"mldtmf" + editLog?.customerLogId} modalActive={!!editLog} modalTitle={"Edit Log"} modalDescription={"Message"}
                onConfirm={editMessageModalConfirm} message={editLog?.message} largeView={true} />
        </React.Fragment>
    )
}
